import { useQuery } from '@tanstack/react-query'
import { type CSSProperties, useState } from 'react'
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom'

import { activeIdForPath, navForContext, type NavEntry } from './nav.js'
import { getSystemStatus, messageFor, statusOverrideFrom } from './system-status.js'
import { apiClient } from '../../api/client.js'
import { useSession } from '../../auth/session.js'
import { useAuthStore } from '../../auth/store.js'
import { AppShell, Button, DegradedBanner, Toaster } from '../../design-system/index.js'
import { ErrorBoundary } from '../../lib/ErrorBoundary.js'
import { useAvatar } from '../../lib/useAvatar.js'
import { ThemeToggle } from '../../theme/ThemeToggle.js'
import { getAccountProfile } from '../account/account-data.js'
import {
  getAdminConciergeThreads,
  totalAdminConciergeUnread,
} from '../admin/admin-concierge-data.js'
import { getConciergeSummary } from '../concierge/concierge-data.js'
import { getWaitingCount } from '../inbox/inbox-data.js'
import { getNotifications } from '../notifications/notifications-data.js'

// The authenticated frame every product page renders inside. Role-aware: the
// session decides the context (tenant workspace vs INHERTA operator console),
// which drives the nav, the chip, and the per-tenant accent. Logout lives in the
// top-bar actions (directly clickable) so e2e/login.spec.ts can sign out.

export function AppLayout(): JSX.Element {
  const session = useSession()
  const navigate = useNavigate()
  const location = useLocation()
  const clear = useAuthStore((s) => s.clear)
  const [collapsed, setCollapsed] = useState(false)

  // App-wide system status. getSystemStatus() polls the readiness probe; the
  // `?systemStatus=degraded|offline` override still forces the banner so the
  // degraded/offline states stay reviewable on demand. Mockup 61.
  const { data: statusData, refetch: refetchStatus } = useQuery({
    queryKey: ['system-status'],
    queryFn: getSystemStatus,
    refetchInterval: 30_000,
  })
  const systemStatus = statusOverrideFrom(location.search) ?? statusData?.status ?? 'operational'

  // Concierge unread → the "Soporte" nav badge. Tenants read it from the cheap
  // summary endpoint; account managers sum the unread across their client threads
  // (the same query the operator inbox uses, so the cache is shared). Both poll.
  //
  // Gated on canManageWorkspace for the tenant side: GET /concierge/summary
  // requires `tenant.concierge` (owner/admin), so for an Asesor this polled a
  // guaranteed 403 every 60 seconds to badge a nav entry they can no longer
  // see. React Query swallowed it, which made it invisible rather than harmless.
  //
  // ALSO gated on mfaVerified: `tenant.concierge` is a manage-only permission,
  // so an owner/admin who has not enrolled a second factor is refused it. This
  // ambient poll must not be the thing that discovers that — the api client
  // bounces every auth_mfa_enrollment_required to /account/security, and a 60s
  // background timer doing it would yank a person out of whatever page they
  // are on. They reach the enrollment screen by USING a manage surface.
  const isTenant = session?.context === 'tenant'
  const isAdmin = session?.context === 'admin'
  const { data: conciergeSummary } = useQuery({
    queryKey: ['concierge', 'summary'],
    queryFn: getConciergeSummary,
    enabled: isTenant && session?.canManageWorkspace === true && session.mfaVerified,
    refetchInterval: 60_000,
  })
  const { data: adminThreads } = useQuery({
    queryKey: ['admin-concierge', 'threads'],
    queryFn: getAdminConciergeThreads,
    enabled: isAdmin,
    refetchInterval: 60_000,
  })
  const conciergeUnread = isAdmin
    ? totalAdminConciergeUnread(adminThreads ?? [])
    : (conciergeSummary?.unread ?? 0)

  // Unread notifications → the top-bar bell dot. `AppShell`/`TopBar` have
  // always supported `hasNotifications`, but nothing ever passed it, so the
  // bell could not light up: there was NO ambient signal anywhere in the shell
  // that a customer was waiting for a human. The only working badge was the
  // concierge one (INHERTA talking to the client), not the client's own customer.
  // Same 60s cadence as the concierge badge — both are ambient counters, not
  // the working surface (the inbox itself polls at 15s).
  const { data: notifData } = useQuery({
    queryKey: ['notifications', 'feed'],
    queryFn: getNotifications,
    refetchInterval: 60_000,
  })
  const unreadNotifications = notifData?.unreadCount ?? 0
  const hasUnreadNotifications = unreadNotifications > 0

  // C4: the Bandeja badge — customers waiting for a HUMAN, the loudest thing
  // in a client's day. Tenant contexts only (staff have their own worklist);
  // same ambient 60s cadence as the other counters.
  const { data: waitingData } = useQuery<number>({
    queryKey: ['inbox', 'waiting-count'],
    queryFn: getWaitingCount,
    enabled: session?.context === 'tenant',
    refetchInterval: 60_000,
  })
  const waitingCount = waitingData ?? 0

  // All signed-in contexts need this row: tenant users for the workspace
  // name/accent, everyone (platform staff included) for hasAvatar. Hooks stay
  // above the session-null return — rules of hooks.
  const { data: profile } = useQuery({
    queryKey: ['account', 'me'],
    queryFn: getAccountProfile,
    enabled: session !== null,
    staleTime: 5 * 60_000,
  })

  // The chip's photo, if the account has one. Gated on hasAvatar so a person
  // without a photo never fires the GET (and 404s) on every page.
  const selfId = useAuthStore((s) => s.user?.id)
  const avatarUrl = useAvatar(selfId, profile?.hasAvatar)

  if (session === null) return <Navigate to="/login" replace />

  const baseNav = navForContext(
    session.context,
    session.canManageWorkspace,
    session.vertical,
    session.platformRole,
  )
  const nav: NavEntry[] = baseNav.map((n) => {
    if ((n.id === 'messages' || n.id === 'admin-messages') && conciergeUnread > 0)
      return { ...n, badge: conciergeUnread }
    if (n.id === 'inbox' && waitingCount > 0) return { ...n, badge: waitingCount }
    return n
  })
  const activeId = activeIdForPath(nav, location.pathname)
  const activeItem = nav.find((n) => n.id === activeId)
  const homePath = nav[0]?.path ?? '/home'

  // The real workspace name and accent. session.tenant is decoded from the JWT,
  // which carries neither, so it only supplies the first-frame fallback. Without
  // this every client's console reads 'Mi espacio' in their own product.
  // Operators have no tenant workspace at all, so this stays off for them.
  const workspace = session.tenant
    ? {
        name: profile?.workspaceName ?? session.tenant.name,
        accent: profile?.workspaceAccent ?? session.tenant.accent,
      }
    : undefined

  async function onLogout(): Promise<void> {
    // Tell the server first — without this the session row lingers forever in
    // "Sesiones activas" and the refresh token stays usable for 14 days. Best
    // effort with a short timeout: logout must never hang on a dead network,
    // and the local clear always happens.
    try {
      await apiClient.post('/auth/logout', {}, { timeout: 3000 })
    } catch {
      // Offline or already-invalid token — proceed with the local clear.
    }
    clear()
    void navigate('/login', { replace: true })
  }

  return (
    <div
      className="h-dvh overflow-hidden"
      style={workspace ? ({ '--accent': workspace.accent } as CSSProperties) : undefined}
    >
      <AppShell
        context={session.context}
        {...(workspace ? { workspace } : {})}
        nav={nav}
        activeId={activeId}
        user={{
          name: session.name,
          role: session.role,
          initials: session.initials,
          avatarUrl,
        }}
        breadcrumb={activeItem ? [{ label: activeItem.label }] : []}
        collapsed={collapsed}
        onToggleCollapse={() => setCollapsed((c) => !c)}
        onBrandClick={() => void navigate(homePath)}
        onUserClick={() => void navigate('/account/profile')}
        onBellClick={() => void navigate('/notificaciones')}
        hasNotifications={hasUnreadNotifications}
        notificationCount={unreadNotifications}
        onNavSelect={(id) => {
          const entry = nav.find((n) => n.id === id)
          if (entry) void navigate(entry.path)
        }}
        topBarActions={
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Button variant="secondary" size="sm" onClick={() => void onLogout()}>
              Cerrar sesión
            </Button>
          </div>
        }
      >
        {systemStatus !== 'operational' && (
          <DegradedBanner
            severity={systemStatus === 'offline' ? 'sens' : 'warn'}
            message={messageFor(systemStatus)}
            onRetry={() => void refetchStatus()}
            className="mb-[22px]"
          />
        )}
        {/* Page-level boundary: a render throw in one page shows a recoverable
            fallback in the content area (nav + top bar stay usable), and the
            `key` remounts it on navigation so moving away clears the error. */}
        <ErrorBoundary key={location.pathname}>
          <Outlet />
        </ErrorBoundary>
      </AppShell>
      <Toaster />
    </div>
  )
}
